/**
 * The recorded voice: what the browser and the server must agree on.
 *
 * The server voices every word, example sentence and dialogue line once
 * (Google's Chirp 3 HD voices) and keeps each as a file named by
 * `ttsKey(role, text)`. The browser computes the same name for what it is
 * about to say and plays that file; only when there is none does it fall
 * back to the device's own voice. So the text must be cleaned the same way
 * on both sides (`speakable`), and the voice chosen the same way
 * (`dialogueVoices`) — a one-character difference is a file that never
 * plays.
 */
/** Bump to have every file made again (a better voice, a new speaking rate). */
export const TTS_VERSION = 1;
/** Two women's voices and two men's, so a dialogue of two women still has two voices. */
export const TTS_ROLES = ['f1', 'f2', 'm1', 'm2'];
/** Words and narration. */
export const DEFAULT_ROLE = 'f1';
/**
 * The Google Chirp 3 HD voice for each role. Diana chose Aoede as the main
 * voice (2026-10-18). The voice is part of every file name, so swapping one
 * re-records only that voice's lines.
 */
export const TTS_VOICES = {
    f1: 'pl-PL-Chirp3-HD-Aoede',
    f2: 'pl-PL-Chirp3-HD-Kore',
    m1: 'pl-PL-Chirp3-HD-Charon',
    m2: 'pl-PL-Chirp3-HD-Puck',
};
/** Text as it should be read aloud: no stage directions, no Markdown marks, no slashes. */
export function speakable(text) {
    return text
        .replace(/\[[^\]]*\]/g, ' ')
        .replace(/[*_`]/g, '')
        // „komentator / komentatorka”: read as two words with a pause, not „ukośnik”.
        .replace(/\s*\/\s*/g, ', ')
        .replace(/\s+/g, ' ')
        .trim()
        .normalize('NFC');
}
/** cyrb53: a small, fast 53-bit hash — the same in Node and in any browser, no crypto API needed. */
function cyrb53(str) {
    let h1 = 0xdeadbeef;
    let h2 = 0x41c6ce57;
    for (let i = 0; i < str.length; i++) {
        const ch = str.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
    h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
    h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16);
}
/** The file name of one piece of speech: version, voice, and the text it says. */
export function ttsKey(role, text) {
    const voice = (TTS_VOICES[role].split('-').pop() ?? role).toLowerCase();
    return `v${TTS_VERSION}-${voice}-${cyrb53(speakable(text))}`;
}
/** Male nouns and names that end in -a, which would otherwise read as female. */
const MALE_IN_A = new Set(['kuba', 'tata', 'kolega', 'mężczyzna', 'barnaba', 'kosma', 'poeta', 'logopeda', 'sędzia', 'satelita', 'starosta', 'wojewoda']);
/**
 * Whether a speaker label is a woman: „Kobieta 1”, „Prowadząca”, „Mama”,
 * „Sprzedawczyni”, „Pani Nowak” — and not „Kuba”, „Tata”, „Kierowca”,
 * „Turysta”, „Pan Kowalski”. Unlabelled lines are read by the narrator.
 */
export function speakerIsFemale(speaker) {
    const s = speaker.toLowerCase().replace(/[\d\s.:]+$/, '').trim();
    if (!s)
        return null;
    if (/^pani\b/.test(s))
        return true;
    if (/^pan\b/.test(s))
        return false;
    const last = s.split(/\s+/).pop() ?? s;
    if (MALE_IN_A.has(last))
        return false;
    // „Kierowca”, „sprzedawca” are men; „prowadząca” (-ąca) is a woman.
    if (/(ysta|ista|[^ą]ca)$/.test(last))
        return false;
    if (/(a|yni|ini)$/.test(last))
        return true;
    return false;
}
/**
 * Which voice says each speaker's lines in one dialogue. Women take f1 then
 * f2, men m1 then m2, in the order they first speak; a third woman shares
 * f1 again. Narration and unlabelled lines get the default voice.
 */
export function dialogueVoices(lines) {
    const out = new Map();
    let women = 0;
    let men = 0;
    for (const { speaker } of lines) {
        if (out.has(speaker))
            continue;
        const female = speakerIsFemale(speaker);
        if (female === null)
            out.set(speaker, DEFAULT_ROLE);
        else if (female)
            out.set(speaker, women++ % 2 === 0 ? 'f1' : 'f2');
        else
            out.set(speaker, men++ % 2 === 0 ? 'm1' : 'm2');
    }
    return out;
}
